import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import CrmModal from '../clientModule/components/CrmModal';
import { IconEdit, IconTrash, IconPlus } from '../clientModule/components/SvgIcons';

const VENDOR_CATEGORIES = [
  'Marketing', 'Office Expense', 'Utilities', 'Maintenance', 'Freelance',
  'Software / SaaS', 'Travel', 'Food & Entertainment', 'Inventory', 'Other',
];

function VendorFormModal({ vendor, onClose, onSaved }) {
  const isEdit = Boolean(vendor);
  const [form, setForm] = useState({
    name: vendor?.name || '',
    category: vendor?.category || '',
    notes: vendor?.notes || '',
    status: vendor?.status || 'Active',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  function set(k, v) { setForm((p) => ({ ...p, [k]: v })); }

  async function handleSave(e) {
    e?.preventDefault();
    if (!form.name.trim()) { setErr('Vendor name is required'); return; }
    setBusy(true); setErr('');
    try {
      if (isEdit) {
        await api.put(`/api/vendors/${vendor.id}`, form);
      } else {
        await api.post('/api/vendors', form);
      }
      onSaved();
    } catch (ex) {
      setErr(ex.message || 'Failed to save vendor');
    } finally {
      setBusy(false);
    }
  }

  return (
    <CrmModal
      onClose={onClose}
      title={isEdit ? `Edit Vendor — ${vendor.name}` : 'Add New Vendor'}
      subtitle="Vendors appear in the Expense Transaction dropdown"
      maxWidth={520}
      footer={
        <>
          <button type="button" className="cm-btn-outline" onClick={onClose}>Cancel</button>
          <button type="button" className="cm-btn-emerald" onClick={handleSave} disabled={busy}>
            {busy ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Vendor'}
          </button>
        </>
      }
    >
      <form onSubmit={handleSave}>
        {err && <div className="err" style={{ marginBottom: 14, fontWeight: 700 }}>{err}</div>}
        <div className="cm-form-section-card">
          <div className="cm-field-group">
            <label>Vendor / Paid To Name *</label>
            <input
              autoFocus
              required
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="e.g. F Social Flip, Meta Ads, ABC Printing…"
            />
          </div>
          <div className="cm-field-group">
            <label>Expense Category</label>
            <select value={form.category} onChange={(e) => set('category', e.target.value)}>
              <option value="">— Select category —</option>
              {VENDOR_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="cm-field-group">
            <label>Status</label>
            <select value={form.status} onChange={(e) => set('status', e.target.value)}>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
          <div className="cm-field-group">
            <label>Notes (Optional)</label>
            <textarea
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              rows={2}
              placeholder="e.g. Monthly Meta ad spends, freelance work…"
            />
          </div>
        </div>
      </form>
    </CrmModal>
  );
}

export default function VendorManagement() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('All');
  const [toast, setToast] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const loadVendors = () => {
    setLoading(true);
    api.get('/api/vendors')
      .then((data) => setVendors(data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(loadVendors, []);

  const handleDelete = async (vendor) => {
    try {
      await api.del(`/api/vendors/${vendor.id}`);
      showToast(`"${vendor.name}" deleted`);
      setConfirmDelete(null);
      loadVendors();
    } catch (err) {
      alert(err.message || 'Failed to delete vendor');
    }
  };

  const allCategories = [...new Set(vendors.map((v) => v.category).filter(Boolean))];

  const filtered = vendors.filter((v) => {
    const matchSearch = !search || v.name.toLowerCase().includes(search.toLowerCase()) ||
      (v.category && v.category.toLowerCase().includes(search.toLowerCase()));
    const matchCat = catFilter === 'All' || v.category === catFilter;
    return matchSearch && matchCat;
  });

  const activeCount = vendors.filter((v) => v.status === 'Active').length;
  const categories = [...new Set(vendors.map((v) => v.category).filter(Boolean))].length;

  return (
    <div>
      {toast && (
        <div style={{ background: 'var(--chip-bg)', color: 'var(--brand)', padding: '10px 18px', borderRadius: 10, marginBottom: 16, fontWeight: 700, border: '1px solid var(--line)' }}>
          ✓ {toast}
        </div>
      )}

      {/* Stats Row */}
      <div className="cm-ref-stats-row" style={{ marginBottom: 24 }}>
        <div className="cm-ref-stat-card">
          <div className="cm-ref-stat-label">TOTAL VENDORS</div>
          <div className="cm-ref-stat-val">{vendors.length}</div>
        </div>
        <div className="cm-ref-stat-card">
          <div className="cm-ref-stat-label">ACTIVE VENDORS</div>
          <div className="cm-ref-stat-val" style={{ color: 'var(--brand)' }}>{activeCount}</div>
        </div>
        <div className="cm-ref-stat-card">
          <div className="cm-ref-stat-label">EXPENSE CATEGORIES</div>
          <div className="cm-ref-stat-val">{categories}</div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="cm-blue-info-banner" style={{ marginBottom: 20 }}>
        <span style={{ fontSize: '1.1rem' }}>💡</span>
        <div>
          <strong>How Vendors work:</strong> Every vendor added here will automatically appear in the <strong>"Vendor / Paid To"</strong> dropdown when adding an Expense Transaction. This makes your expense reports fully filterable by vendor.
        </div>
      </div>

      {/* Toolbar */}
      <div className="cm-ref-toolbar">
        <input
          className="cm-ref-search"
          placeholder="Search vendor name or category…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <select className="cm-select-filter" value={catFilter} onChange={(e) => setCatFilter(e.target.value)}>
            <option value="All">All Categories</option>
            {allCategories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <button className="cm-btn-emerald" onClick={() => { setEditingVendor(null); setShowForm(true); }}>
            <IconPlus size={16} /> Add Vendor
          </button>
        </div>
      </div>

      {/* Vendor Table */}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)', fontWeight: 600 }}>Loading vendors…</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', background: 'var(--card)', borderRadius: 12, border: '2px dashed #e2e8f0', color: 'var(--muted)' }}>
          No vendors found.{' '}
          <button className="cm-btn-outline" style={{ fontSize: '0.85rem', marginLeft: 8 }} onClick={() => setShowForm(true)}>
            + Add First Vendor
          </button>
        </div>
      ) : (
        <div className="cm-table-card">
          <table className="cm-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Vendor / Paid To</th>
                <th>Expense Category</th>
                <th>Notes</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((v, idx) => (
                <tr key={v.id}>
                  <td style={{ color: '#94a3b8', fontWeight: 700, fontSize: '0.8rem' }}>{idx + 1}</td>
                  <td style={{ fontWeight: 800, color: 'var(--ink)' }}>{v.name}</td>
                  <td>
                    {v.category ? (
                      <span className="chip" style={{ background: 'var(--chip-bg)', color: 'var(--brand)', fontWeight: 700 }}>{v.category}</span>
                    ) : (
                      <span style={{ color: '#94a3b8', fontSize: '0.82rem' }}>—</span>
                    )}
                  </td>
                  <td style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>{v.notes || '—'}</td>
                  <td>
                    <span style={{
                      background: v.status === 'Active' ? '#f0f4ff' : '#f8fafc',
                      color: v.status === 'Active' ? '#1c3d8a' : '#94a3b8',
                      padding: '3px 10px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 700
                    }}>
                      {v.status}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="cm-action-icon-btn" title="Edit Vendor" onClick={() => { setEditingVendor(v); setShowForm(true); }}>
                        <IconEdit size={15} />
                      </button>
                      <button className="cm-action-icon-btn" title="Delete Vendor" style={{ color: 'var(--ink)' }} onClick={() => setConfirmDelete(v)}>
                        <IconTrash size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showForm && (
        <VendorFormModal
          vendor={editingVendor}
          onClose={() => { setShowForm(false); setEditingVendor(null); }}
          onSaved={() => { setShowForm(false); setEditingVendor(null); showToast(editingVendor ? 'Vendor updated' : 'Vendor added'); loadVendors(); }}
        />
      )}

      {/* Delete Confirm */}
      {confirmDelete && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--card)', borderRadius: 16, padding: 32, maxWidth: 400, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 12px', color: 'var(--ink)' }}>Delete Vendor?</h3>
            <p style={{ color: 'var(--muted)', marginBottom: 24 }}>Delete <strong>"{confirmDelete.name}"</strong>? Existing expense records using this vendor name will not be affected.</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="cm-btn-outline" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button className="cm-btn-outline" style={{ background: 'var(--bg)', color: 'var(--ink)', borderColor: 'var(--line)' }} onClick={() => handleDelete(confirmDelete)}>
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
